/**
 * Keyboard geometry for UI that is fixed to the bottom of the screen.
 *
 * One owner for the two questions a bottom-anchored element has to ask:
 * "is the keyboard up?" (so the bottom nav can get out of the way) and
 * "how far do I have to lift to sit ON the keyboard rather than float above
 * it?" (so a sticky action bar lands flush against it).
 *
 * Detection uses both signals because neither covers every target:
 *  - `body.keyboard-visible` is written by the Capacitor keyboard listeners
 *    (src/services/capacitor/index.ts) and is the reliable native signal —
 *    but on native the WebView is resized, so visualViewport sees nothing.
 *  - visualViewport is the only signal in a mobile browser, where the layout
 *    viewport does not shrink.
 *
 * See src/docs/MOBILE_HEADER_FOOTER_KEYBOARD_GAP.md for the native background.
 */

import { useEffect, useState } from 'react';
import { getKeyboardOverlap, setAppHeight } from '@/utils/viewport';

/**
 * A visual-viewport shrink smaller than this is a URL bar collapsing or an
 * accessory bar appearing, not a keyboard.
 */
const KEYBOARD_MIN_OVERLAP_PX = 120;

export interface KeyboardInset {
  /** True while the on-screen keyboard is up, on native and in the browser. */
  isKeyboardVisible: boolean;
  /**
   * Pixels of the layout viewport the keyboard covers — the offset from
   * `bottom: 0` that puts an element flush on top of it. Zero on native and
   * on Android Chrome, where the container itself is already resized.
   */
  keyboardOverlap: number;
}

export function useKeyboardInset(): KeyboardInset {
  const [inset, setInset] = useState<KeyboardInset>({
    isKeyboardVisible: false,
    keyboardOverlap: 0,
  });

  useEffect(() => {
    // Keep --app-height correct: Safari PWAs report stale dvh values after the
    // keyboard dismisses (src/utils/viewport.ts).
    setAppHeight();

    let restoreTimer: number | undefined;

    const sync = () => {
      const keyboardOverlap = getKeyboardOverlap();
      const isKeyboardVisible =
        document.body.classList.contains('keyboard-visible') ||
        keyboardOverlap > KEYBOARD_MIN_OVERLAP_PX;

      setInset((prev) =>
        prev.isKeyboardVisible === isKeyboardVisible && prev.keyboardOverlap === keyboardOverlap
          ? prev
          : { isKeyboardVisible, keyboardOverlap }
      );

      // When the keyboard closes, give Safari a beat to finish restoring the
      // viewport before re-locking the layout to window.innerHeight.
      if (!isKeyboardVisible) {
        window.clearTimeout(restoreTimer);
        restoreTimer = window.setTimeout(setAppHeight, 100);
      }
    };

    const handleWindowResize = () => {
      // Orientation changes and browser-chrome resizes both land here.
      setAppHeight();
      sync();
    };

    sync();

    const observer = new MutationObserver(sync);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    const viewport = window.visualViewport;
    viewport?.addEventListener('resize', sync);
    viewport?.addEventListener('scroll', sync);
    window.addEventListener('resize', handleWindowResize);

    return () => {
      window.clearTimeout(restoreTimer);
      observer.disconnect();
      viewport?.removeEventListener('resize', sync);
      viewport?.removeEventListener('scroll', sync);
      window.removeEventListener('resize', handleWindowResize);
    };
  }, []);

  return inset;
}
